import { useCallback, useRef, type KeyboardEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { findTopNav, type SubNavId, type TopNavId } from './settings-tree'
import { nextRovingIndex } from './nextRovingIndex'
import './PreferencesSideNav.scss'

interface PreferencesSideNavProps {
  topNavId: TopNavId
  activeSubNavId: SubNavId
  onSelect: (subNavId: SubNavId) => void
}

/**
 * Left-hand sidebar listing the SubNav entries for the currently active
 * TopNav. Selected item gets a muted background plus a soft pink halo —
 * the Figma "blur-lg pink/200" accent glow.
 *
 * Keyboard support follows the ARIA Tabs pattern (vertical orientation):
 * - `ArrowUp` / `ArrowDown` cycle through SubNavs (wrapping at the ends)
 * - `Home` / `End` jump to the first / last SubNav
 * - Roving `tabIndex`: only the active item is in the document tab order
 */
export function PreferencesSideNav({ topNavId, activeSubNavId, onSelect }: PreferencesSideNavProps) {
  const { t } = useTranslation()
  const containerRef = useRef<HTMLUListElement | null>(null)
  const topNav = findTopNav(topNavId)
  const activeIndex = topNav?.subNav.findIndex((entry) => entry.id === activeSubNavId) ?? 0

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (!topNav) return
      const next = nextRovingIndex(event.key, activeIndex, topNav.subNav.length, 'vertical')
      if (next === null) return
      event.preventDefault()
      const nextId = topNav.subNav[next].id as SubNavId
      onSelect(nextId)
      // Focus follows selection — find the button by id and shift focus
      // so subsequent arrow presses operate on the newly active item.
      requestAnimationFrame(() => {
        const button = containerRef.current?.querySelector<HTMLElement>(
          `[data-subnav-id="${nextId}"]`,
        )
        button?.focus()
      })
    },
    [activeIndex, onSelect, topNav],
  )

  if (!topNav) return null

  return (
    // `data-section` lets the SCSS pick the matching branded glow tint per
    // TopNav (pink for app-experience, teal for account-data, …) without
    // duplicating selectors per item.
    <aside className="preferences-sidenav" data-section={topNavId} aria-label={t(topNav.i18nKey)}>
      <ul
        ref={containerRef}
        className="preferences-sidenav__list"
        role="tablist"
        aria-orientation="vertical"
        onKeyDown={handleKeyDown}
      >
        {topNav.subNav.map((entry) => {
          const Icon = entry.icon
          const isActive = entry.id === activeSubNavId
          return (
            <li key={entry.id} className="preferences-sidenav__item">
              <button
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                data-subnav-id={entry.id}
                className={`preferences-sidenav__button ${isActive ? 'preferences-sidenav__button--active' : ''}`.trim()}
                onClick={() => onSelect(entry.id as SubNavId)}
              >
                {isActive && (
                  <span aria-hidden="true" className="preferences-sidenav__glow" />
                )}
                <span className="preferences-sidenav__icon">
                  <Icon size={16} strokeWidth={1.75} />
                </span>
                <span className="preferences-sidenav__label">{t(entry.i18nKey)}</span>
              </button>
            </li>
          )
        })}
      </ul>
    </aside>
  )
}
